import React, { useState, useEffect } from 'react';
import NavBar from '../../components/Navbar/NavBar';
import Footer from '../../components/Footer';
import useDocTitle from '../../utils/useDocTitle';
import SideMenu from '../../components/SideMenu';
import AudioPlayer from '../../components/AudioPlayer';
import { FaStepBackward, FaStepForward } from 'react-icons/fa';
import Card from '../../components/Cards/Card';
import ContactUs from '../../components/ContactUs';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';

const AudioDescriptions = () => {
  useDocTitle('Audio Descriptions');
  const { language } = useLanguage();

  const [mainPageData, setMainPageData] = useState(null);
  const [audioDescriptions, setAudioDescriptions] = useState([]);
  const [currentDescriptionId, setCurrentDescriptionId] = useState(null);
  const [audioState, setAudioState] = useState({
    currentTrack: null,
    currentTrackIndex: 0,
    tracks: [],
  });
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0); // Add this state for video navigation
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMainPageData = async () => {
      try {
        const response = await axios.get('https://annexpilot.com/dashboard/wp-json/wp/v2/aud_desc_mainpage');
        const data = response.data.find(
          (entry) => entry.page_language[0]?.name.toLowerCase() === language.toLowerCase()
        );
        setMainPageData({
          title: data.title.rendered,
          mainTitle: data.main_title,
          mainText: data.main_text,
          backgroundImage: data.main_image.guid,
        });
      } catch (error) {
        console.error('Error fetching main page data:', error);
      }
    };

    const fetchAudioDescriptions = async () => {
      try {
        const response = await axios.get('https://annexpilot.com/dashboard/wp-json/wp/v2/audiodescriptions');
        const filteredData = response.data.filter((desc) =>
          desc.page_language.some((lang) => lang.name.toLowerCase() === language.toLowerCase())
        );
        setAudioDescriptions(filteredData);
        if (filteredData.length > 0) {
          initializeAudioPlayer(filteredData[0]);
        }
      } catch (error) {
        console.error('Error fetching audio descriptions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMainPageData();
    fetchAudioDescriptions();
  }, [language]);

  const initializeAudioPlayer = (description) => {
    setCurrentDescriptionId(description.id);
    setAudioState({
      tracks: description.audio_files || [],
      currentTrack: description.audio_files?.[0] || null,
      currentTrackIndex: 0,
    });
  };
  console.log(audioState.currentTrack)

  const selectTrack = (index) => {
    setAudioState((prevState) => ({
      ...prevState,
      currentTrack: prevState.tracks[index],
      currentTrackIndex: index,
    }));
  };
  console.log(audioState.currentTrack)

  const nextTrack = () => {
    setAudioState((prevState) => {
      const nextIndex = (prevState.currentTrackIndex + 1) % prevState.tracks.length;
      return {
        ...prevState,
        currentTrack: prevState.tracks[nextIndex],
        currentTrackIndex: nextIndex,
      };
    });
  };

  const previousTrack = () => {
    setAudioState((prevState) => {
      const prevIndex =
        (prevState.currentTrackIndex - 1 + prevState.tracks.length) % prevState.tracks.length;
      return {
        ...prevState,
        currentTrack: prevState.tracks[prevIndex],
        currentTrackIndex: prevIndex,
      };
    });
  };

  const nextVideo = () => {
    setCurrentVideoIndex((prevIndex) => prevIndex + 1);
  };

  const previousVideo = () => {
    setCurrentVideoIndex((prevIndex) => prevIndex - 1);
  };

  const menuItems = [
    { href: '#overview', label: mainPageData?.mainTitle || (language === 'English' ? 'Overview' : 'Përmbledhje') },
    {
      href: '#programs',
      label:
        language === 'English'
          ? 'Listen to our audio description resources'
          : 'Dëgjoni burimet tona të përshkrimit zanor',
    },
    {
      href: '#cards',
      label:
        language === 'English'
          ? 'View our audio-described productions'
          : 'Shikoni produksionet tona me përshkrimit zanor',
    },
  ];

  if (loading || !mainPageData) {
    return <div>{language === 'English' ? 'Loading...' : 'Duke u ngarkuar...'}</div>;
  }

  const videoLinks =
  audioDescriptions.find((desc) => desc.id === currentDescriptionId)?.video_links || [];
const currentVideoLink =
  videoLinks.length > 0
    ? videoLinks[currentVideoIndex]?.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')
    : '';

  return (
    <>
      <NavBar />

      <div
          className="relative w-full h-96 bg-cover bg-center flex items-center justify-center"
          style={{
          backgroundImage: `url(${mainPageData.backgroundImage})`,
        }}
        role="img"
        aria-label={
          language === 'English'
            ? 'Background image of Audio Descriptions section'
            : 'Imazhi i sfondit të seksionit të Përshkrimeve Zanore'
        }
      >
        <div className="absolute inset-0 bg-black opacity-60"></div>
        <div className="relative z-10 text-center text-white px-4">
        <h1 className="text-5xl font-bold" tabIndex="0">
        {language === 'English' ? 'Audio Descriptions' : 'Përshkrimet Zanore'}
          </h1>
        </div>
      </div>

      <div className="flex flex-col md:flex-row min-h-screen">
        <aside
          className="lg:w-1/4 bg-gray-100 p-4 lg:p-8 sticky top-16 lg:h-screen"
          aria-label={
            language === 'English'
              ? 'Sidebar navigation for Audio Descriptions'
              : 'Navigimi anësor për Përshkrimet Zanore'
          }
        >
          <SideMenu
            backLink="/artistic-program"
            backText={language === 'English' ? 'Artistic Programs' : 'PROGRAMET ARTISTIKE'}
            menuItems={menuItems}
          />
        </aside>

        <div className="w-full lg:w-3/4 p-4 lg:p-8">
          <section id="overview" className="container-inner mb-12">
            <h2 className="text-3xl font-semibold mb-6" tabIndex="0">
              {mainPageData.mainTitle}
            </h2>
            <div
              className="text-lg text-gray-600 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: mainPageData.mainText }}
            ></div>
          </section>

          <section id="programs" className="container-inner mb-12">
            <h2 className="text-3xl font-semibold mb-6" tabIndex="0">
              {language === 'English'
                ? 'Listen to our audio description resources'
                : 'Dëgjoni burimet tona të përshkrimit zanor'}
            </h2>

            {/* Loop through all audio descriptions */}
            {audioDescriptions.map((description) => (
              <div
                key={description.id}
                className="bg-gray-100 p-6 rounded-md shadow-md mb-8"
                role="region"
                aria-label={
                  language === 'English'
                    ? `Audio description for ${description.audio_description_title}`
                    : `Përshkrimi zanor për ${description.audio_description_title}`
                }
              >
                <div className="flex items-center mb-4">
                  <img
                    src={description.card_image?.guid || '/placeholder.jpg'}
                    alt={
                      language === 'English' ? 'Audio description image' : 'Imazh për përshkrimin zanor'
                    }
                    className="w-32 h-32 object-cover rounded-md mr-4"
                  />
                  <div>
                    <h4 className="text-xl font-semibold">{description.audio_description_title}</h4>
                  </div>
                </div>

                {description.audio_files?.length > 0 ? (
                  <>
                    <div className="flex items-center justify-left space-x-2 mb-4">
                      <button
                        onClick={() => initializeAudioPlayer(description)}
                        className="text-primary text-lg font-medium underline"
                        aria-label={
                          language === 'English'
                            ? `Play first track for ${description.audio_description_title}`
                            : `Luaj përshkrimin e parë për ${description.audio_description_title}`
                        }
                      >
                        {language === 'English' ? 'Play First Track' : 'Luaj përshkrimin e parë'}
                      </button>
                    </div>

                    <ol
                      className="border-t border-gray-300 mt-4"
                      aria-label={
                        language === 'English'
                          ? `Track list for ${description.audio_description_title}`
                          : `Lista e këngëve për ${description.audio_description_title}`
                      }
                    >
                      {description.audio_files.map((track, index) => (
                        <li
                          key={track.ID}
                          className="flex justify-between py-2 px-4 border-b border-gray-200 cursor-pointer hover:bg-blue-50"
                        >
                          <span className="text-md">
                            {index + 1}. {track.post_title}
                          </span>
                          <button
                            className="text-primary text-sm font-medium underline"
                            onClick={() => selectTrack(index)}
                            aria-label={
                              language === 'English'
                                ? `Play track ${track.post_title}`
                                : `Luaj këngën ${track.post_title}`
                            }
                          >
                            {language === 'English' ? 'Play' : 'Luaj'}
                          </button>
                        </li>
                      ))}
                    </ol>
                  </>
                ) : (
                  <p className="text-gray-500">
                    {language === 'English'
                      ? 'No audio files available for this description.'
                      : 'Nuk ka skedarë audio të disponueshëm për këtë përshkrim.'}
                  </p>
                )}
              </div>
            ))}
          </section>


  
            <section id="cards" className="container-inner mb-12">
              <h2 className="text-3xl font-semibold mb-6" tabIndex="0">
                {language === 'English'
                  ? 'Audio Descriptions'
                  : 'Përshkrimet Zanore'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
                {audioDescriptions.map((program) => (
                  <Card
                    key={program.id}
                    title={program.audio_description_title}
                    description={program.description}
                    imgSrc={program.card_image?.guid || '/placeholder.jpg'}
                    imgAlt={
                      program.card_image?.post_excerpt ||
                      (language === 'English' ? 'No image available' : 'Imazh jo i disponueshëm')
                    }
                    linkText={language === 'English' ? 'Learn more' : 'Shiko më shumë'}
                    linkPath={`/resources/audio-description/${program.id}`}
                    ariaLabel={
                      language === 'English'
                        ? `Go to audio description program ${program.audio_description_title}`
                        : `Shko te programi i përshkrimit zanor ${program.audio_description_title}`
                    }
                    onClick={() => {
                      initializeAudioPlayer(program);
                    }}
                  />
                ))}
              </div>
            </section>
  
            {/* Contact Us Section */}
            <ContactUs />
          </div>
        </div>
  
        <Footer />
      </>
    );
  };
  
  export default AudioDescriptions;  